import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../component/common/Loader';
import SearchInput from '../../component/common/Input/SearchInput';
import Table from '../../component/common/Table';
import Pagination from '../../component/common/table2/Pagination';
import { TableCard } from '../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Shared, searchable users table for the Perks section.
 *
 * Columns are programme-shaped rather than metric-shaped, so this is separate
 * from the sleep/breathwork/habit/marketplace tables. The company page supplies
 * its own company/v1 `fetcher`.
 *
 * EVERY user of the population appears, including those on nothing — "who is
 * missing out" is the question this table answers, so filtering them out would
 * remove the answer. They show dashes and sort last.
 */
const PerksUsersTable = ({
  onOpenUser,
  tableName,
  fetcher,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        // A zero is a real answer here ("on nothing"), but a row of four zeros
        // reads as broken data — dashes make the empty case legible.
        const mapped = (response.data || []).map((row) => ({
          ...row,
          programmesText: row.programmes > 0 ? `${row.programmes} of 4` : '-',
          salaryText: row.salarySacrifice > 0 ? `${row.salarySacrifice}` : '-',
          healthText: row.healthScheme > 0 ? `${row.healthScheme}` : '-',
          perksText: row.customPerks > 0 ? `${row.customPerks}` : '-',
          callsText: row.supportCalls > 0 ? `${row.supportCalls}` : '-'
        }));
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('PerksUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    {
      title: 'Programmes',
      key: 'programmesText',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: 'Salary Sacrifice', key: 'salaryText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Health Scheme', key: 'healthText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Custom Perks', key: 'perksText', sortable: false, align: 'center', type: 'text3' },
    { title: 'EAP Calls', key: 'callsText', sortable: false, align: 'center', type: 'text3' },
    ...(onOpenUser
      ? [{ title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }]
      : [])
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

PerksUsersTable.propTypes = {
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  fetcher: PropTypes.func.isRequired,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default PerksUsersTable;
